import React, { createContext, useContext, useState, useEffect } from 'react';

// Create cart context
const CartContext = createContext();

// Custom hook to use the cart context
export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Cart state with items, cart totals, etc.
  const [cart, setCart] = useState({
    items: [],
    totalItems: 0,
    subtotal: 0,
    isOpen: false
  });
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  
  const openSearch = () => {
    setIsSearchOpen(true);
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
  };

  const openMobileMenu = () => {
    setIsMobileMenuOpen(true);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Load cart from localStorage on initial render
  useEffect(() => {
    const savedCart = localStorage.getItem('institute_lab_cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        setCart(prevCart => ({
          ...prevCart,
          items: parsedCart.items || [],
          totalItems: calculateTotalItems(parsedCart.items || []),
          subtotal: calculateSubtotal(parsedCart.items || [])
        }));
      } catch (error) {
        console.error('Error parsing saved cart:', error);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('institute_lab_cart', JSON.stringify({
      items: cart.items,
      // Don't save UI state like isOpen
    }));
  }, [cart.items]);

  // Helper function to calculate total items
  const calculateTotalItems = (items) => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  // Helper function to calculate subtotal with proper decimal handling
  const calculateSubtotal = (items) => {
    const total = items.reduce((sum, item) => {
      const price = parseFloat(item.price) || 0;
      return sum + (price * item.quantity);
    }, 0);
    
    // Round to whole number
    return Math.round(total);
  };

  // Helper function to format price display
  const formatPrice = (price) => {
    const numPrice = parseFloat(price) || 0;
    const selectedCurrency = localStorage.getItem('selectedCurrency') || 'MKD';
    
    if (selectedCurrency === 'MKD') {
      return Math.round(numPrice).toString();
    } else {
      // Convert to EUR (1 EUR = 61.5 MKD)
      const eurPrice = numPrice / 61.5;
      return eurPrice.toFixed(2).replace('.', ',');
    }
  };

  // Add item to cart
  const addToCart = (product, quantity = 1, selectedSize = null) => {
    setCart(prevCart => {
      const variationId = product.variation_id;

      const existingItemIndex = prevCart.items.findIndex(
        item => item.id === product.id && item.variation_id === variationId
      );

      let newItems;

      if (existingItemIndex !== -1) {
        // Update existing item
        newItems = [...prevCart.items];
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          quantity: newItems[existingItemIndex].quantity + quantity
        };
      } else {
        // Add new item
        const newItem = {
          id: product.id,
          variation_id: variationId,
          name: product.name,
          price: product.price,
          image: product.images && product.images.length > 0 ? product.images[0].src : null,
          quantity,
          selectedSize,
          variation: product.variation,
          acf: product.acf,
          formula: product.formula
        };

        newItems = [...prevCart.items, newItem];
      }

      return {
        ...prevCart,
        items: newItems,
        totalItems: calculateTotalItems(newItems),
        subtotal: calculateSubtotal(newItems)
      };
    });
  };

  // Remove item from cart
  const removeFromCart = (itemId, selectedSize = null, variationId = null) => {
    setCart(prevCart => {
      const newItems = prevCart.items.filter(item => {
        // Match by product ID and variation
        if (variationId) {
          return !(item.id === itemId && item.variation_id === variationId);
        }
        return !(item.id === itemId && item.selectedSize === selectedSize);
      });

      return {
        ...prevCart,
        items: newItems,
        totalItems: calculateTotalItems(newItems),
        subtotal: calculateSubtotal(newItems)
      };
    });
  };

  // Update item quantity
  const updateQuantity = (itemId, quantity, selectedSize = null, variationId = null) => {
    if (quantity <= 0) {
      removeFromCart(itemId, selectedSize, variationId);
      return;
    }

    setCart(prevCart => {
      const newItems = prevCart.items.map(item => {
        // Match by product ID and variation
        const matchesVariation = variationId
          ? (item.id === itemId && item.variation_id === variationId)
          : (item.id === itemId && item.selectedSize === selectedSize);

        if (matchesVariation) {
          return { ...item, quantity };
        }
        return item;
      });

      return {
        ...prevCart,
        items: newItems,
        totalItems: calculateTotalItems(newItems),
        subtotal: calculateSubtotal(newItems)
      };
    });
  };

  // Open cart
  const openCart = () => {
    setCart(prevCart => ({
      ...prevCart,
      isOpen: true
    }));
  };

  // Close cart
  const closeCart = () => {
    setCart(prevCart => ({
      ...prevCart,
      isOpen: false
    }));
  };

  // Clear cart
  const clearCart = () => {
    setCart({
      items: [],
      totalItems: 0,
      subtotal: 0,
      isOpen: false
    });
  };

  // Value to be provided to consumers
  const value = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    openCart,
    closeCart,
    clearCart,
    formatPrice, // Add this helper function to the context
    isSearchOpen,
    openSearch,
    closeSearch,
    isMobileMenuOpen,
    openMobileMenu,
    closeMobileMenu
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;